/**
 * Team Context
 * Manages active team membership and access control
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useCallback,
} from 'react';
import { doc, getDoc, onSnapshot, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getIdToken, type User } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseError } from 'firebase/app';
import { db } from '@/lib/firebase';
import { useAuth } from './AuthContext';
import { ensureTeamBootstrap } from '@/services/teamBootstrap.service';
import { ensureUserBootstrap } from '@/services/ensureUserBootstrap.service';
import { getUserActiveMemberships } from '@/services/membershipIndex.service';
import { getActiveTeamPreference } from '@/services/userPreferences.service';
import { resolveTeamEntitlement } from '@/lib/entitlement';
import { resolveTeamDeactivation } from '@/lib/entitlement/resolver';
import { logger } from '@/lib/logger';
import { maskId } from '@/lib/logger/maskId';
import { isPlatformAdminUser } from '@/lib/authz/isPlatformAdminUser';
import { setSentryUser } from '@/monitoring/sentry';
import { isOAuthUser, getPrimaryProvider } from '@/lib/auth/providerUtils';
import { isOwner, isAdminRole } from '@/lib/authz/teamAuthz';
import type { TeamRole } from '@/lib/authz/teamAuthz';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Boot stability: Membership load status state machine
 */
export type MembershipLoadStatus = 'idle' | 'loading' | 'slow' | 'ready' | 'error';

/**
 * Boot stability: Deterministic error codes for membership loading
 */
export type BootErrorCode =
  | 'MEMBERSHIP_TIMEOUT'
  | 'MEMBERSHIP_PERMISSION_RACE'
  | 'MEMBERSHIP_QUERY_FAILED';

/**
 * Boot stability: Retry helper for membership queries with token refresh
 * Retries on transient permission/auth errors with exponential backoff
 */
async function retryMembershipQuery<T>(
  fn: () => Promise<T>,
  user: User,
  bootRunId: number,
  currentBootRunId: () => number,
  context: string
): Promise<T> {
  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [500, 1000, 2000];
  const startTime = Date.now();

  const isTransientError = (err: unknown): boolean => {
    // Only retry on specific Firebase error codes (no string matching)
    if (err instanceof FirebaseError) {
      return err.code === 'permission-denied' || err.code === 'unauthenticated';
    }
    return false;
  };

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    // Check if boot cycle is still current (cancel-safe)
    if (bootRunId !== currentBootRunId()) {
      logger.info(`[${context}] Boot cycle changed, aborting retry`, {
        bootRunId,
        currentBootRunId: currentBootRunId(),
      });
      throw new Error('Boot cycle changed');
    }

    try {
      // Force token refresh before retry attempts (not on first attempt)
      if (attempt > 0) {
        const delay = RETRY_DELAYS[attempt - 1] || 2000;
        await new Promise((resolve) => setTimeout(resolve, delay));

        try {
          await getIdToken(user, true); // Force refresh
          logger.info(`[${context}] Token refreshed, retry ${attempt}/${MAX_RETRIES}`, {
            bootRunId,
            elapsedMs: Date.now() - startTime,
          });
        } catch (tokenErr) {
          logger.warn(`[${context}] Token refresh failed, continuing anyway`, tokenErr);
        }
      }

      const result = await fn();

      if (attempt > 0) {
        logger.info(`[${context}] Retry succeeded`, {
          bootRunId,
          retryAttempt: attempt,
          elapsedMs: Date.now() - startTime,
        });
      }

      return result;
    } catch (err) {
      const isLastAttempt = attempt === MAX_RETRIES;

      if (!isTransientError(err) || isLastAttempt) {
        // Not a transient error OR max retries exhausted
        if (isLastAttempt && isTransientError(err)) {
          const errorCode = err instanceof FirebaseError ? err.code : 'unknown';
          logger.error(`[${context}] Max retries exhausted on permission error`, {
            bootRunId,
            retryAttempt: attempt,
            elapsedMs: Date.now() - startTime,
            errorCode,
            error: err instanceof Error ? err.message : String(err),
          });
        }
        throw err;
      }

      // Transient error, will retry
      const errorCode = err instanceof FirebaseError ? err.code : 'unknown';
      logger.info(`[${context}] Transient error, will retry`, {
        bootRunId,
        retryAttempt: attempt + 1,
        maxRetries: MAX_RETRIES,
        errorCode,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  throw new Error('Unreachable');
}

/**
 * Check if current route is an invite page
 * Used to skip team loading for invite flow
 */
function isInviteRoute(pathname: string): boolean {
  return pathname.startsWith('/invite/');
}

/**
 * Check if current route is an admin-only page
 * Used to allow platform admins without teams to access /admin/ routes
 */
function isAdminRoute(pathname: string): boolean {
  return pathname.startsWith('/admin');
}

/**
 * Mask sensitive IDs in production logs
 * Shows first 4 chars + "..." in production, full ID in development
 */
function maskId(id: string | null | undefined): string {
  if (!id) return 'none';
  if (process.env.NODE_ENV === 'development') return id;
  return id.length > 4 ? `${id.substring(0, 4)}...` : id;
}

export interface TeamMember {
  uid: string;
  email: string;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
  joinedAt: Date;
}

/**
 * Minimal team data needed for guards and context
 */
export interface TeamData {
  id: string;
  name: string;
  plan: 'free' | 'paid';
  createdAt?: Date | null;
  onboardingCompletedAt?: Date | null;
  onboardingVersion?: number;
  hasSampleData?: boolean;
  /** Business type selected during onboarding */
  businessType?: 'dealer' | 'garage' | 'dealer_garage' | null;
  /** When sample data was seeded (for banner visibility) */
  sampleDataSeededAt?: Date | null;
  /** When sample data was deleted */
  sampleDataDeletedAt?: Date | null;
  /** When user dismissed the sample data banner */
  sampleDataBannerDismissedAt?: Date | null;
  /** FIX M5: Current onboarding step (server-side persistence) */
  onboardingCurrentStep?: string | null;
  /** AI credits state (for useAiCredits hook) */
  aiCredits?: {
    monthlyLimit: number;
    remaining: number;
    periodKey: string;
    lastResetAt?: unknown;
  };
  /** Entitlement data (for plan tier resolution) */
  entitlement?: {
    tier: string;
    state?: string;
    activatedAt?: unknown;
    source?: string;
    cancelStatus?: string;
    cancelEffectiveAt?: unknown;
  };
}

export interface TeamContextValue {
  activeTeamId: string | null;
  team: TeamData | null;
  loading: boolean;
  isMember: boolean;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'viewer' | null;
  memberData: TeamMember | null;
  setActiveTeamId: (teamId: string) => Promise<void>;
  error: string | null;
  /** True if team exists but is missing canonical entitlement data */
  entitlementInvalid: boolean;
  /** Reason why entitlement is invalid (for UI display) */
  entitlementInvalidReason: string | null;
  /** Optimistic flag set when onboarding completes, before Firestore updates */
  onboardingJustCompleted: boolean;
  /** Call this when onboarding completes to prevent guard redirect loops */
  markOnboardingCompleted: () => void;
  /** User's onboarding state from Firestore profile (e.g., 'invited', 'needs_team', null) */
  onboardingState: string | null;
  /** True if user is in onboarding flow (needs_team, invited, or no team yet) - guards should show loader, not error */
  isOnboardingInProgress: boolean;
  /**
   * True if the user's Firestore profile has an activeTeamId set.
   * This is known BEFORE the membership query completes and is used to
   * distinguish "transient permission error during auth-token propagation"
   * from "user genuinely has no team".
   * Guards must NOT show the error screen when profileHasTeam is true.
   */
  profileHasTeam: boolean;
  /** True if team is deactivated after subscription cancellation */
  isDeactivated: boolean;
  /**
   * True if the current user's membership has active=false.
   * Distinct from isDeactivated (team-level) — this is member-level access control.
   * When true, the app must show AccessDisabledScreen and block all team reads.
   */
  isMemberDisabled: boolean;
  /** Boot stability: Membership load status state machine */
  membershipLoadStatus: MembershipLoadStatus;
  /** Boot stability: Deterministic error code for membership loading failures */
  bootErrorCode: BootErrorCode | null;
  /** Boot stability: Retry boot after hard timeout or error */
  retryBoot: () => void;
}

export const TeamContext = createContext<TeamContextValue | undefined>(undefined);

interface TeamProviderProps {
  children: ReactNode;
}

export function TeamProvider({ children }: TeamProviderProps) {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [activeTeamId, setActiveTeamIdState] = useState<string | null>(null);
  const [team, setTeam] = useState<TeamData | null>(null);

  // Track previous role and plan to detect changes and invalidate cache
  const prevRoleRef = useRef<'owner' | 'admin' | 'manager' | 'member' | null>(null);
  const prevPlanRef = useRef<'free' | 'paid' | null>(null);
  // Separate loading states for user profile, team document, and membership
  const [userProfileLoading, setUserProfileLoading] = useState(true);
  const [teamDocLoading, setTeamDocLoading] = useState(false);
  const [membershipLoading, setMembershipLoading] = useState(false);
  // Track when membership query has definitively completed (success or error)
  // This prevents watchdog from setting NO_TEAM while query is still in-flight
  const [membershipQueryCompleted, setMembershipQueryCompleted] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [role, setRole] = useState<'owner' | 'admin' | 'manager' | 'member' | null>(null);
  const [memberData, setMemberData] = useState<TeamMember | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Optimistic flag to prevent guard redirect loops after onboarding completion
  const [onboardingJustCompleted, setOnboardingJustCompleted] = useState(false);
  // FIX H2: Track OAuth bootstrap completion to prevent race condition
  // Membership queries should wait until bootstrap completes for OAuth users
  const [oauthBootstrapComplete, setOauthBootstrapComplete] = useState(false);
  // Track user's onboarding state from Firestore profile
  const [onboardingState, setOnboardingState] = useState<string | null>(null);
  // Track whether the user's Firestore profile has an activeTeamId set.
  // Set to true as soon as we read a non-empty activeTeamId from the profile doc,
  // regardless of whether the membership query has completed.
  // This lets guards distinguish a transient permission error (profileHasTeam=true)
  // from a genuine no-team state (profileHasTeam=false).
  const [profileHasTeam, setProfileHasTeam] = useState(false);
  // Track if team has invalid/missing entitlement (canonical-only mode)
  const [entitlementInvalid, setEntitlementInvalid] = useState(false);
  const [entitlementInvalidReason, setEntitlementInvalidReason] = useState<string | null>(null);
  // Track if team is deactivated after subscription cancellation
  const [isDeactivated, setIsDeactivated] = useState(false);
  // Track if the current user's membership is disabled (active=false)
  const [isMemberDisabled, setIsMemberDisabled] = useState(false);

  // Boot stability: Membership load status state machine
  const [membershipLoadStatus, setMembershipLoadStatus] = useState<MembershipLoadStatus>('idle');
  const [bootErrorCode, setBootErrorCode] = useState<BootErrorCode | null>(null);

  // Boot stability: Unique ID for each boot cycle to prevent stale async updates
  const bootRunIdRef = useRef(0);
  const bootStartTimeRef = useRef(Date.now());
  const softTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hardTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bootFinalizedRef = useRef(false);

  // Combined loading state: true if any critical state is still loading
  // This prevents the flicker where activeTeamId is set but team is still null
  // Membership loading is included to ensure we don't render until we know the user's role
  //
  // IMPORTANT: We removed the teamDataPending check because it created a stuck loading state:
  // If activeTeamId was set but teamDocLoading was false and team was null, loading would be
  // true forever. The teamDocLoading flag is now the authoritative signal for team loading.
  // When activeTeamId is set, we immediately set teamDocLoading=true BEFORE setting activeTeamId,
  // so there's no gap where teamDataPending would be needed.
  const loading = userProfileLoading || teamDocLoading || membershipLoading;

  // Track previous membership state to detect removal while browsing
  const wasMemberRef = useRef(false);

  // Track which teams have been backfilled this session to prevent infinite loops
  // Key: teamId, Value: true if backfill was attempted
  const backfilledTeamsRef = useRef<Set<string>>(new Set());

  // Check if we're on an invite route - skip team loading to avoid permission errors
  // Use window.location since useLocation requires Router context
  const isOnInviteRoute = typeof window !== 'undefined' && isInviteRoute(window.location.pathname);

  // Track if OAuth bootstrap has been attempted for this user session
  const oauthBootstrapAttemptedRef = useRef<string | null>(null);

  // Track if initial load has completed for this user (to distinguish initial vs realtime updates)
  const initialLoadCompletedRef = useRef<string | null>(null);

  // Timeout watchdog ref for failsafe
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Boot stability: Clear all watchdog timers
   * Called when boot reaches terminal state (ready/error) or restarts
   */
  const clearBootWatchdogs = useCallback(() => {
    if (softTimeoutRef.current) {
      clearTimeout(softTimeoutRef.current);
      softTimeoutRef.current = null;
    }
    if (hardTimeoutRef.current) {
      clearTimeout(hardTimeoutRef.current);
      hardTimeoutRef.current = null;
    }
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  /**
   * DEV-ONLY: Validate state transitions to catch illegal overwrites
   * Allowed transitions:
   * - idle -> loading
   * - loading -> slow, ready, error
   * - slow -> ready, error
   * - error -> loading (only via retryBoot)
   * - ready -> loading (only via session change/retryBoot)
   *
   * Forbidden transitions:
   * - ready -> slow, error (without restart)
   * - error -> slow, ready (without restart)
   * - idle -> slow, error, ready (direct)
   */
  const prevStatusRef = useRef<MembershipLoadStatus>('idle');
  const validateStateTransition = useCallback(
    (nextStatus: MembershipLoadStatus, reason: string) => {
      if (!import.meta.env.DEV) return;

      const prevStatus = prevStatusRef.current;

      // Define allowed transitions
      const allowedTransitions: Record<MembershipLoadStatus, MembershipLoadStatus[]> = {
        idle: ['loading'],
        loading: ['slow', 'ready', 'error'],
        slow: ['ready', 'error'],
        error: ['loading'], // Only via retryBoot
        ready: ['loading'], // Only via session change/retryBoot
      };

      const isAllowed = allowedTransitions[prevStatus]?.includes(nextStatus) ?? false;

      if (!isAllowed && prevStatus !== nextStatus) {
        logger.error(`[BootSanity] ILLEGAL_TRANSITION: ${prevStatus} -> ${nextStatus}`, {
          reason,
          bootRunId: bootRunIdRef.current,
          finalized: bootFinalizedRef.current,
          prevStatus,
          nextStatus,
          allowedFrom: allowedTransitions[prevStatus],
        });

        // Add Sentry breadcrumb if available (dev only)
        if (typeof window !== 'undefined' && (window as any).Sentry) {
          (window as any).Sentry.addBreadcrumb({
            category: 'boot-sanity',
            message: `Illegal state transition: ${prevStatus} -> ${nextStatus}`,
            level: 'error',
            data: { reason, bootRunId: bootRunIdRef.current, prevStatus, nextStatus },
          });
        }
      }

      // Update previous status ref
      prevStatusRef.current = nextStatus;
    },
    []
  );

  /**
   * SINGLE TRANSITION WRAPPER: Enforces validation on all status updates
   * This is the ONLY function that should call setMembershipLoadStatus
   * All direct calls must go through this wrapper to prevent bypass
   */
  const updateMembershipStatus = useCallback(
    (next: MembershipLoadStatus, reason: string) => {
      validateStateTransition(next, reason);
      setMembershipLoadStatus(next);
    },
    [validateStateTransition]
  );

  // Refs to track current state for watchdog (avoids stale closures)
  const currentStateRef = useRef({
    userProfileLoading: true,
    teamDocLoading: false,
    membershipLoading: false,
    membershipQueryCompleted: false,
    activeTeamId: null as string | null,
    team: null as TeamData | null,
    isMember: false,
    error: null as string | null,
    role: null as 'owner' | 'admin' | 'manager' | 'member' | null,
  });

  // Helper to get auth provider for logging
  const getProviderForLogging = (): string => {
    if (!user) return 'none';
    const providers = user.providerData?.map((p) => p.providerId) || [];
    if (providers.includes('google.com')) return 'google.com';
    if (providers.includes('microsoft.com')) return 'microsoft.com';
    if (providers.includes('password')) return 'password';
    return providers[0] || 'unknown';
  };

  // ============================================================================
  // OAUTH BOOTSTRAP EFFECT
  // For Google/Microsoft users, ensure Firestore profile exists before loading teams.
  // This runs ONCE per user session and creates /users/{uid} if missing.
  // Email/password users already have profiles created during atomicRegistration.
  // ============================================================================
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading || !user) {
      return;
    }

    // Skip on invite routes
    if (isOnInviteRoute) {
      return;
    }

    // Only run for OAuth users (Google, Microsoft)
    if (!isOAuthUser(user)) {
      return;
    }

    // Only run once per user session
    if (oauthBootstrapAttemptedRef.current === user.uid) {
      return;
    }

    const provider = getPrimaryProvider(user);
    logger.info('[TeamProvider] OAuth user detected, running bootstrap', {
      uid: user.uid,
      provider,
      email: user.email,
    });

    // Mark as attempted immediately to prevent duplicate runs
    oauthBootstrapAttemptedRef.current = user.uid;

    // Run bootstrap asynchronously - this creates /users/{uid} if missing
    // and determines if user needs team setup
    // FIX H2: Mark bootstrap as complete so membership queries can proceed
    void ensureUserBootstrap(user).then((result) => {
      logger.info('[TeamProvider] OAuth bootstrap complete', {
        uid: user.uid,
        provider,
        success: result.success,
        hasTeam: result.hasTeam,
        teamId: result.teamId,
        needsTeamSetup: result.needsTeamSetup,
        error: result.error,
      });

      // Mark bootstrap as complete so membership queries can proceed
      setOauthBootstrapComplete(true);

      // If bootstrap found/created a team, the onSnapshot listener will pick it up
      // If user needs team setup, the NO_TEAM error will trigger create-team routing
    });
  }, [user, authLoading, isOnInviteRoute]);

  // Load active team ID from user profile
  // REFACTORED: Use getDoc() for initial resolution to make loading deterministic
  // Then attach onSnapshot for realtime updates AFTER initial load completes
  useEffect(() => {
    // CRITICAL: Wait for auth to finish loading before making any decisions
    if (authLoading) {
      setUserProfileLoading(true);
      return;
    }

    if (!user) {
      // Increment boot run ID to cancel any in-flight operations
      bootRunIdRef.current += 1;
      clearBootWatchdogs(); // Clear timers on logout
      setActiveTeamIdState(null);
      setUserProfileLoading(false);
      setTeamDocLoading(false);
      setMembershipQueryCompleted(false);
      updateMembershipStatus('idle', 'logout_reset');
      setBootErrorCode(null);
      setIsMember(false);
      setRole(null);
      setMemberData(null);
      setError(null);
      setTeam(null);
      setOnboardingJustCompleted(false);
      setOnboardingState(null);
      setProfileHasTeam(false);
      initialLoadCompletedRef.current = null;
      return;
    }

    // Increment boot run ID for new user session
    bootRunIdRef.current += 1;
    bootStartTimeRef.current = Date.now();
    bootFinalizedRef.current = false; // Reset finalized flag for new boot
    clearBootWatchdogs(); // Clear any stale timers from previous boot

    // Skip team loading on invite routes
    if (isOnInviteRoute) {
      logger.info('[TeamProvider] Skipping team loading on invite route');
      setUserProfileLoading(false);
      return;
    }

    // FIX H2: For OAuth users, wait for bootstrap to complete before querying memberships
    // This prevents the race condition where membership query runs before profile exists
    if (isOAuthUser(user) && !oauthBootstrapComplete) {
      logger.info('[TeamProvider] Waiting for OAuth bootstrap to complete before loading team', {
        uid: user.uid,
      });
      setUserProfileLoading(false);
      return;
    }

    const provider = getProviderForLogging();
    const route = typeof window !== 'undefined' ? window.location.pathname : 'unknown';

    // Check if initial load already completed for this user (avoid re-running on StrictMode)
    if (initialLoadCompletedRef.current === user.uid) {
      logger.info('[TeamProvider] Initial load already completed, skipping', { uid: user.uid });
      return;
    }

    setUserProfileLoading(true);
    setError(null);

    const userDocRef = doc(db, 'users', user.uid);
    let unsubscribe: (() => void) | null = null;

    // Helper to process user profile data (shared between getDoc and onSnapshot)
    const processUserProfile = async (
      exists: boolean,
      userData: Record<string, unknown> | undefined,
      isInitialLoad: boolean
    ) => {
      if (process.env.NODE_ENV === 'development') {
        logger.info('[TeamProvider] processUserProfile', {
          exists,
          isInitialLoad,
          provider,
          route,
          uid: user.uid,
          activeTeamId: userData?.activeTeamId,
        });
      }

      if (exists && userData) {
        const profileTeamId = userData.activeTeamId as string | undefined;
        const profileOnboardingState = userData.onboardingState as string | undefined;
        const pendingInvite = userData.pendingInvite as
          | { inviteId?: string; teamId?: string }
          | undefined;

        // FIX: Always update onboardingState from profile so guards can check it
        setOnboardingState(profileOnboardingState || null);

        // Track whether the profile has a team ID — used to detect transient permission
        // errors vs genuine no-team state (see isOnboardingInProgress below).
        setProfileHasTeam(!!profileTeamId);

        // Check if user is in invite flow
        if (profileOnboardingState === 'invited' || pendingInvite) {
          logger.info('[TeamProvider] User is in invite flow, skipping team search');
          setActiveTeamIdState(null);
          setIsMember(false);
          setRole(null);
          // Mark as completed to prevent watchdog from firing
          setMembershipQueryCompleted(true);
          return;
        }

        // MEMBERSHIP INDEX IS SOURCE OF TRUTH
        // Always query memberships first, then validate/use activeTeamId as preference

        // Boot stability: Transition to loading state
        const currentBootRunId = bootRunIdRef.current;
        bootFinalizedRef.current = false; // Reset finalized flag at boot start
        updateMembershipStatus('loading', 'membership_query_start');
        bootStartTimeRef.current = Date.now();

        logger.info('[TeamProvider] Membership query start', {
          bootRunId: currentBootRunId,
          profileTeamId: maskId(profileTeamId),
        });

        try {
          // Use retry helper for membership query with token refresh
          const activeMemberships = await retryMembershipQuery(
            () => getUserActiveMemberships(user.uid),
            user,
            currentBootRunId,
            () => bootRunIdRef.current,
            'TeamProvider:MembershipQuery'
          );
          const activeMembershipTeamIds = new Set(activeMemberships.map((m) => m.teamId));

          // Mark membership query as completed (success)
          // Single Finalizer: Only set terminal state if not already finalized
          if (bootRunIdRef.current === currentBootRunId && !bootFinalizedRef.current) {
            bootFinalizedRef.current = true;
            setMembershipQueryCompleted(true);
            updateMembershipStatus('ready', 'membership_success');
            setBootErrorCode(null);
            clearBootWatchdogs(); // Clear timers on success
          } else {
            // Boot cycle changed or already finalized, skip state update
            return;
          }

          const elapsedMs = Date.now() - bootStartTimeRef.current;
          logger.info('[TeamProvider] Membership query complete', {
            bootRunId: currentBootRunId,
            membershipStatus: 'ready',
            count: activeMemberships.length,
            teamIds: activeMemberships.map((m) => maskId(m.teamId)),
            profileTeamId: maskId(profileTeamId),
            elapsedMs,
          });

          if (activeMemberships.length > 0) {
            // User has memberships - determine which team to use
            let selectedTeamId: string;

            // CANONICAL: Read activeTeamId from users/{uid}.activeTeamId (root field)
            // One-time migration: if missing, check legacy preferences and copy forward
            if (profileTeamId && activeMembershipTeamIds.has(profileTeamId)) {
              // Canonical path has valid activeTeamId
              selectedTeamId = profileTeamId;
              logger.info('[TeamProvider] Using activeTeamId from canonical path (validated)', {
                teamId: maskId(selectedTeamId),
              });
            } else {
              // Canonical path missing or invalid - check legacy preferences for migration
              let legacyTeamId: string | null = null;
              try {
                legacyTeamId = await getActiveTeamPreference(user.uid);
              } catch {
                // Non-fatal: preferences doc may not exist
              }

              if (legacyTeamId && activeMembershipTeamIds.has(legacyTeamId)) {
                // Found valid teamId in legacy preferences - migrate to canonical path
                selectedTeamId = legacyTeamId;
                logger.info(
                  '[TeamProvider] Migrating activeTeamId from legacy preferences to canonical path',
                  {
                    teamId: maskId(selectedTeamId),
                  }
                );

                // One-time migration: copy to canonical path
                try {
                  await setDoc(
                    userDocRef,
                    {
                      activeTeamId: selectedTeamId,
                      updatedAt: serverTimestamp(),
                    },
                    { merge: true }
                  );
                  logger.info(
                    '[TeamProvider] Successfully migrated activeTeamId to canonical path'
                  );
                } catch (migrationErr) {
                  logger.warn(
                    '[TeamProvider] Failed to migrate activeTeamId to canonical path:',
                    migrationErr
                  );
                }
              } else {
                // CONTRACT §10: Deterministic Active Team Selection
                // If membership count === 1 and activeTeamId is null/invalid,
                // auto-set activeTeamId to that single team deterministically.
                // This prevents users from getting stuck at team picker.
                const firstMembership = activeMemberships[0];
                if (!firstMembership) {
                  throw new Error('Unexpected: activeMemberships[0] is undefined');
                }
                selectedTeamId = firstMembership.teamId;

                // Self-heal: update profile with valid teamId (only if different)
                // Guard: prevent unnecessary writes and potential loops
                const needsSelfHeal = profileTeamId !== selectedTeamId;
                if (needsSelfHeal) {
                  logger.info('[TeamProvider] §10 Auto-selecting single team', {
                    teamId: maskId(selectedTeamId),
                    membershipCount: activeMemberships.length,
                    staleProfileTeamId: maskId(profileTeamId),
                  });
                  try {
                    await setDoc(
                      userDocRef,
                      {
                        activeTeamId: selectedTeamId,
                        updatedAt: serverTimestamp(),
                      },
                      { merge: true }
                    );
                    logger.info('[TeamProvider] Auto-selection persisted to profile');
                  } catch (updateErr) {
                    logger.warn('[TeamProvider] Could not persist auto-selection:', updateErr);
                  }
                } else {
                  logger.info('[TeamProvider] Using first membership (profile already correct)', {
                    teamId: maskId(selectedTeamId),
                  });
                }
              }
            }

            if (isInitialLoad) {
              setTeamDocLoading(true);
            }
            setActiveTeamIdState(selectedTeamId);
          } else {
            // No active memberships - check if this is a platform admin on admin route
            const isPlatformAdmin = isPlatformAdminUser(userData);
            const currentRoute = typeof window !== 'undefined' ? window.location.pathname : '';
            const isOnAdminRoute = isAdminRoute(currentRoute);

            if (isPlatformAdmin && isOnAdminRoute) {
              // ADMIN-ONLY MODE: Platform admin without team on /admin/* route
              // Skip team reads entirely - admin can access admin pages without team context
              logger.info(
                '[TeamProvider] Platform admin on admin route without team - admin-only mode',
                {
                  uid: maskId(user.uid),
                  route: currentRoute,
                }
              );
              setActiveTeamIdState(null);
              setIsMember(false);
              setRole(null);
              setError(null); // No error - this is a valid state for admins
              // Do NOT set NO_TEAM error - admin-only mode is valid
            } else if (isPlatformAdmin && !isOnAdminRoute) {
              // Platform admin without team but NOT on admin route
              // They need to go to /admin/* or create a team
              logger.info(
                '[TeamProvider] Platform admin without team - should navigate to /admin/*',
                {
                  uid: maskId(user.uid),
                  route: currentRoute,
                }
              );
              setActiveTeamIdState(null);
              setIsMember(false);
              setRole(null);
              setError('NO_TEAM'); // Will trigger redirect to onboarding/create-team
            } else {
              // Normal user without team - definitively NO_TEAM
              logger.warn('[TeamProvider] No active memberships found (definitive)', {
                uid: maskId(user.uid),
              });
              setActiveTeamIdState(null);
              setIsMember(false);
              setRole(null);
              setError('NO_TEAM');
            }
          }
        } catch (membershipErr) {
          // Membership query failed after retries
          const errMessage =
            membershipErr instanceof Error ? membershipErr.message : 'Unknown error';
          const isTransientError =
            membershipErr instanceof FirebaseError &&
            (membershipErr.code === 'permission-denied' ||
              membershipErr.code === 'unauthenticated');
          const elapsedMs = Date.now() - bootStartTimeRef.current;

          // Determine error code
          const errorCode: BootErrorCode = isTransientError
            ? 'MEMBERSHIP_PERMISSION_RACE'
            : 'MEMBERSHIP_QUERY_FAILED';

          // Mark membership query as completed (with error)
          // Single Finalizer: Only set terminal state if not already finalized
          if (bootRunIdRef.current === currentBootRunId && !bootFinalizedRef.current) {
            bootFinalizedRef.current = true;
            setMembershipQueryCompleted(true);
            updateMembershipStatus('error', 'permission_race_exhausted');
            clearBootWatchdogs(); // Clear timers on error
            setBootErrorCode(errorCode);

            logger.error('[TeamProvider] Membership query failed after retries', {
              bootRunId: currentBootRunId,
              membershipStatus: 'error',
              errorCode,
              firebaseCode: membershipErr instanceof FirebaseError ? membershipErr.code : 'unknown',
              error: errMessage,
              elapsedMs,
            });
          } else {
            // Boot cycle changed or already finalized, skip state update
            logger.info(
              '[TeamProvider] Skipping error state update (boot cycle changed or finalized)',
              {
                bootRunId: currentBootRunId,
                currentBootRunId: bootRunIdRef.current,
                finalized: bootFinalizedRef.current,
              }
            );
            return;
          }

          if (isTransientError) {
            setError('MEMBERSHIP_PERMISSION_DENIED');
          } else {
            setError('MEMBERSHIP_QUERY_FAILED');
          }

          // Do NOT set NO_TEAM - we don't know if user has memberships or not
          // Show error state instead of redirecting to onboarding
          setActiveTeamIdState(null);
          setIsMember(false);
          setRole(null);
        }
      } else {
        // User profile doesn't exist - search for teams via membership index
        logger.warn('[TeamProvider] User profile not found, querying membership index...');
        try {
          const activeMemberships = await getUserActiveMemberships(user.uid);

          // Mark membership query as completed
          setMembershipQueryCompleted(true);

          if (activeMemberships.length > 0 && activeMemberships[0]) {
            const primaryTeamId = activeMemberships[0].teamId;
            logger.info('[TeamProvider] Found team via membership index (no profile)', {
              teamId: maskId(primaryTeamId),
            });
            if (isInitialLoad) {
              setTeamDocLoading(true);
            }
            setActiveTeamIdState(primaryTeamId);
            // Create user profile
            try {
              await setDoc(userDocRef, {
                activeTeamId: primaryTeamId,
                email: user.email,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
            } catch (createErr) {
              logger.warn('[TeamProvider] Could not create user profile:', createErr);
            }
          } else {
            // No memberships found - definitively NO_TEAM
            logger.warn('[TeamProvider] No memberships found (no profile, definitive NO_TEAM)');
            setActiveTeamIdState(null);
            setIsMember(false);
            setRole(null);
            setError('NO_TEAM');
          }
        } catch (searchErr) {
          // Membership query failed - surface error, don't redirect to onboarding
          const errMessage = searchErr instanceof Error ? searchErr.message : 'Unknown error';
          logger.error('[TeamProvider] Membership query failed (no profile)', {
            uid: maskId(user.uid),
            error: errMessage,
          });

          // Mark membership query as completed (with error)
          setMembershipQueryCompleted(true);

          setActiveTeamIdState(null);
          setIsMember(false);
          setRole(null);
          setError('MEMBERSHIP_QUERY_FAILED');
        }
      }
    };

    // INITIAL LOAD: Use getDoc() for deterministic resolution
    // With retry logic for transient permission errors (auth token propagation race)
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [500, 1000, 2000]; // Exponential backoff in ms

    const isPermissionError = (err: unknown): boolean => {
      // Only check FirebaseError codes, no string matching
      if (err instanceof FirebaseError) {
        return err.code === 'permission-denied' || err.code === 'unauthenticated';
      }
      return false;
    };

    const doInitialLoad = async (retryCount = 0) => {
      // Path-specific logging for Firestore calls (always log for diagnostics)
      logger.info(`[TeamProvider] getDoc users/${user.uid}`, {
        provider,
        route,
        retryCount,
      });

      // On first attempt, ensure auth token is fresh before Firestore call
      // This prevents the race condition where Firestore rejects due to stale/missing token
      // For OAuth users after bootstrap, force refresh to pick up custom claims
      if (retryCount === 0) {
        const shouldForceRefresh = isOAuthUser(user) && oauthBootstrapComplete;
        try {
          await getIdToken(user, /* forceRefresh */ shouldForceRefresh);
          if (shouldForceRefresh) {
            logger.info('[TeamProvider] Token force-refreshed for OAuth user after bootstrap', {
              uid: user.uid,
            });
          }
        } catch (tokenErr) {
          logger.warn('[TeamProvider] Initial token check failed:', tokenErr);
        }
      }

      try {
        const snapshot = await getDoc(userDocRef);
        await processUserProfile(snapshot.exists(), snapshot.data(), true);

        // Mark initial load as completed
        initialLoadCompletedRef.current = user.uid;

        // AFTER initial load: attach onSnapshot for realtime updates
        unsubscribe = onSnapshot(
          userDocRef,
          async (realtimeSnapshot) => {
            // Skip if this is the initial snapshot (we already processed it)
            // onSnapshot fires immediately with cached data, so we need to ignore it
            if (process.env.NODE_ENV === 'development') {
              logger.info('[TeamProvider] Realtime update received');
            }
            await processUserProfile(realtimeSnapshot.exists(), realtimeSnapshot.data(), false);
          },
          (err) => {
            // Only log realtime errors if not a transient permission issue
            if (!isPermissionError(err)) {
              logger.error('[TeamProvider] Realtime listener error:', err);
            }
            // Don't set loading states - initial load already completed
          }
        );
      } catch (err) {
        // RETRY LOGIC: On permission errors, retry with backoff + token refresh
        // This handles the race condition where auth token isn't fully propagated yet
        if (isPermissionError(err) && retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAYS[retryCount] || 2000;
          if (process.env.NODE_ENV === 'development') {
            logger.info('[TeamProvider] Permission error, retrying with token refresh...', {
              retryCount: retryCount + 1,
              maxRetries: MAX_RETRIES,
              delayMs: delay,
            });
          }

          // Wait for backoff delay
          await new Promise((resolve) => setTimeout(resolve, delay));

          // Force token refresh before retrying - this ensures Firestore gets fresh auth
          try {
            await getIdToken(user, /* forceRefresh */ true);
            if (process.env.NODE_ENV === 'development') {
              logger.info('[TeamProvider] Token refreshed successfully');
            }
          } catch (tokenErr) {
            logger.warn('[TeamProvider] Token refresh failed, continuing anyway:', tokenErr);
          }

          return doInitialLoad(retryCount + 1);
        }

        // Only log error after all retries exhausted
        logger.error('[TeamProvider] Error in initial getDoc() after retries:', err);

        // Try to find teams via membership index as fallback
        // Force token refresh first to maximize chances of success
        try {
          await getIdToken(user, /* forceRefresh */ true);
        } catch {
          // Continue anyway - token might still be valid
        }

        try {
          const activeMemberships = await getUserActiveMemberships(user.uid);
          // Mark membership query as completed
          setMembershipQueryCompleted(true);

          if (activeMemberships.length > 0 && activeMemberships[0]) {
            setTeamDocLoading(true);
            setActiveTeamIdState(activeMemberships[0].teamId);
            // Clear the error since we found a team
            setError(null);
          } else {
            setActiveTeamIdState(null);
            setError('NO_TEAM');
          }
        } catch (membershipErr) {
          // Mark membership query as completed (with error)
          setMembershipQueryCompleted(true);
          setActiveTeamIdState(null);

          // Distinguish between permission errors and other errors
          const errMessage =
            membershipErr instanceof Error ? membershipErr.message : 'Unknown error';
          if (isPermissionError(membershipErr)) {
            setError('MEMBERSHIP_PERMISSION_DENIED');
          } else {
            setError('MEMBERSHIP_QUERY_FAILED');
          }
          logger.error('[TeamProvider] Fallback membership query failed:', errMessage);
        }
      }
    };

    // Wrapper to ensure loading is set false after all retries complete
    const doInitialLoadWithCleanup = async () => {
      try {
        await doInitialLoad(0);
      } finally {
        // CRITICAL: Always set loading false after all attempts
        setUserProfileLoading(false);
        if (process.env.NODE_ENV === 'development') {
          logger.info('[TeamProvider] Initial load complete, userProfileLoading=false', {
            uid: user.uid,
            provider,
          });
        }
      }
    };

    void doInitialLoadWithCleanup();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isOnInviteRoute is stable, oauthBootstrapComplete triggers re-run for OAuth users
  }, [user, authLoading, oauthBootstrapComplete]);

  // Load team document data (for onboarding state, plan, etc.)
  // REFACTORED: Use getDoc() for initial resolution to make loading deterministic
  useEffect(() => {
    if (!activeTeamId) {
      setTeam(null);
      setTeamDocLoading(false);
      return;
    }

    // Set loading BEFORE starting the fetch
    setTeamDocLoading(true);

    const teamDocRef = doc(db, 'teams', activeTeamId);
    let unsubscribe: (() => void) | null = null;

    // Helper to process team data
    const processTeamData = async (exists: boolean, data: Record<string, unknown> | undefined) => {
      if (exists && data) {
        // LEGACY BACKFILL: If team is clearly a legacy team but missing onboardingCompletedAt
        const isLegacyTeam = data.hasSampleData === true || data._legacyBackfill === true;
        const needsBackfill = isLegacyTeam && !data.onboardingCompletedAt;
        const alreadyBackfilled = backfilledTeamsRef.current.has(activeTeamId);

        if (needsBackfill && user && !alreadyBackfilled) {
          backfilledTeamsRef.current.add(activeTeamId);
          logger.info(
            '[TeamProvider] Backfilling onboardingCompletedAt for legacy team:',
            activeTeamId
          );
          try {
            await updateDoc(teamDocRef, {
              onboardingCompletedAt: serverTimestamp(),
              onboardingVersion: 1,
              _legacyBackfill: true,
            });
          } catch (backfillErr) {
            logger.warn('[TeamProvider] Failed to backfill legacy team:', backfillErr);
          }
        }

        // Use entitlement resolver to determine plan state
        const entitlement = resolveTeamEntitlement(data);

        // Check for invalid entitlement (missing canonical data)
        if (entitlement.isInvalid) {
          setEntitlementInvalid(true);
          setEntitlementInvalidReason(
            entitlement.invalidReason || 'Team is missing entitlement data'
          );
          logger.warn('[TeamProvider] Team has invalid entitlement:', {
            teamId: activeTeamId,
            reason: entitlement.invalidReason,
          });
        } else {
          setEntitlementInvalid(false);
          setEntitlementInvalidReason(null);
        }

        // Track deactivation status — canonical primary: isActive===false, then entitlement fallbacks
        // resolveTeamDeactivation() is the SINGLE source of truth per contract §2
        setIsDeactivated(resolveTeamDeactivation(data));

        const effectivePlan = entitlement.isFree ? 'free' : 'paid';

        setTeam({
          id: activeTeamId,
          name: (data.name as string) || '',
          plan: effectivePlan,
          createdAt: (data.createdAt as { toDate: () => Date } | undefined)?.toDate() || null,
          onboardingCompletedAt:
            (data.onboardingCompletedAt as { toDate: () => Date } | undefined)?.toDate() || null,
          onboardingVersion: (data.onboardingVersion as number) || undefined,
          hasSampleData: (data.hasSampleData as boolean) || false,
          businessType: (data.businessType as 'dealer' | 'garage' | 'dealer_garage') || null,
          sampleDataSeededAt:
            (data.sampleDataSeededAt as { toDate: () => Date } | undefined)?.toDate() || null,
          sampleDataDeletedAt:
            (data.sampleDataDeletedAt as { toDate: () => Date } | undefined)?.toDate() || null,
          sampleDataBannerDismissedAt:
            (data.sampleDataBannerDismissedAt as { toDate: () => Date } | undefined)?.toDate() ||
            null,
          // AI credits and entitlement for useAiCredits hook
          aiCredits: data.aiCredits as TeamData['aiCredits'],
          entitlement: data.entitlement as TeamData['entitlement'],
        });

        // §19.3 INV-ONBOARDING-2: Reset optimistic bridge flag once Firestore confirms
        // onboardingCompletedAt is set. Without this, the flag stays true for the entire
        // session, permanently suppressing resolver cases 3, 4, and 5.
        if (data.onboardingCompletedAt) {
          setOnboardingJustCompleted(false);
        }
      } else {
        setTeam(null);
      }
    };

    // INITIAL LOAD: Use getDoc() for deterministic resolution
    const doInitialLoad = async () => {
      // Path-specific logging for Firestore calls (always log for diagnostics)
      logger.info(`[TeamProvider] getDoc teams/${activeTeamId}`);

      try {
        const snapshot = await getDoc(teamDocRef);
        await processTeamData(snapshot.exists(), snapshot.data());

        // AFTER initial load: attach onSnapshot for realtime updates
        unsubscribe = onSnapshot(
          teamDocRef,
          async (realtimeSnapshot) => {
            await processTeamData(realtimeSnapshot.exists(), realtimeSnapshot.data());
          },
          (err) => {
            logger.warn('[TeamProvider] Team realtime listener error:', err);
          }
        );
      } catch (err) {
        logger.warn('[TeamProvider] Error loading team document:', err);
        setTeam(null);
        // If team doc is unreadable (permission-denied = deactivated rules, or missing),
        // treat as blocked so the guard routes to team-picker instead of looping.
        const isPermDenied = err instanceof FirebaseError && err.code === 'permission-denied';
        if (isPermDenied) {
          logger.warn(
            '[TeamProvider] Team doc permission-denied — treating as deactivated/blocked'
          );
          setIsDeactivated(true);
        }
      } finally {
        // CRITICAL: Always set loading false in finally block
        setTeamDocLoading(false);
        if (process.env.NODE_ENV === 'development') {
          logger.info('[TeamProvider] Team doc load complete, teamDocLoading=false', {
            teamId: activeTeamId,
          });
        }
      }
    };

    void doInitialLoad();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [activeTeamId, user]);

  // Verify team membership and bootstrap if needed
  // REFACTORED: Use getDoc() for initial resolution to make loading deterministic
  useEffect(() => {
    if (!user || !activeTeamId) {
      setIsMember(false);
      setRole(null);
      setMemberData(null);
      setMembershipLoading(false);
      return;
    }

    // Skip membership verification on invite routes
    if (isOnInviteRoute) {
      setMembershipLoading(false);
      return;
    }

    setMembershipLoading(true);
    setError(null);

    const memberDocRef = doc(db, 'teams', activeTeamId, 'members', user.uid);
    let unsubscribe: (() => void) | null = null;

    // Helper to process membership data
    const processMembershipData = async (
      exists: boolean,
      data: Record<string, unknown> | undefined,
      isInitialLoad: boolean
    ) => {
      if (exists && data) {
        // Check if member has been disabled (active=false)
        const memberActive = data.active !== false; // default true for backward compat
        if (!memberActive) {
          logger.warn('[TeamProvider] User membership is disabled (active=false)', {
            teamId: maskId(activeTeamId),
          });
          setIsMemberDisabled(true);
          setIsMember(false);
          setRole(null);
          setMemberData(null);
          return;
        }
        setIsMemberDisabled(false);

        // V1.2: prefer teamRole (canonical), fall back to role (legacy migration)
        const resolvedRole =
          (data.teamRole as TeamMember['role'] | undefined) ||
          (data.role as TeamMember['role'] | undefined) ||
          (() => {
            logger.warn('[TeamProvider] Missing role/teamRole, defaulting to member');
            return 'member' as const;
          })();

        const member: TeamMember = {
          uid: user.uid,
          email: user.email || '',
          role: resolvedRole,
          joinedAt: (data.joinedAt as { toDate: () => Date } | undefined)?.toDate() || new Date(),
        };

        setIsMember(true);
        wasMemberRef.current = true;
        setRole(member.role);
        setMemberData(member);

        if (isInitialLoad) {
          logger.info('[TeamProvider] User is member of team', {
            teamId: maskId(activeTeamId),
            role: member.role,
          });
        }

        // Update Sentry context
        setSentryUser({ uid: user.uid, teamId: activeTeamId, role: member.role });

        // Bootstrap team defaults if user is owner
        if (isOwner(member.role as TeamRole)) {
          try {
            await ensureTeamBootstrap(activeTeamId, user.uid);
          } catch (err: unknown) {
            const errMsg = err instanceof Error ? err.message : 'Unknown error';
            logger.warn('[TeamProvider] Bootstrap failed:', errMsg);
          }
        }
      } else {
        const wasPreviouslyMember = wasMemberRef.current;

        setIsMember(false);
        wasMemberRef.current = false;
        setRole(null);
        setMemberData(null);

        if (isInitialLoad) {
          logger.warn('[TeamProvider] User is NOT a member of team', {
            teamId: maskId(activeTeamId),
          });
        }

        // Show notification if user was removed while browsing (not on initial load)
        if (wasPreviouslyMember && !isInitialLoad) {
          toast.error('Je bent verwijderd uit dit team', {
            description: 'Neem contact op met de teambeheerder voor meer informatie.',
            duration: 10000,
          });
        }
      }
    };

    // INITIAL LOAD: Use getDoc() for deterministic resolution
    const doInitialLoad = async () => {
      // Path-specific logging for Firestore calls (always log for diagnostics)
      logger.info(`[TeamProvider] getDoc teams/${activeTeamId}/members/${user.uid}`);

      try {
        const snapshot = await getDoc(memberDocRef);
        await processMembershipData(snapshot.exists(), snapshot.data(), true);

        // AFTER initial load: attach onSnapshot for realtime updates
        unsubscribe = onSnapshot(
          memberDocRef,
          async (realtimeSnapshot) => {
            await processMembershipData(realtimeSnapshot.exists(), realtimeSnapshot.data(), false);
          },
          (err: unknown) => {
            logger.error('[TeamProvider] Membership realtime listener error:', err);
          }
        );
      } catch (err: unknown) {
        logger.error('[TeamProvider] Error checking membership:', err);
        const firebaseError = err as { code?: string; message?: string };
        if (firebaseError.code === 'permission-denied') {
          setError('Missing or insufficient permissions.');
        } else {
          setError(firebaseError.message || 'Unknown error');
        }
        setIsMember(false);
        setRole(null);
        setMemberData(null);
      } finally {
        // CRITICAL: Always set loading false in finally block
        setMembershipLoading(false);
        if (process.env.NODE_ENV === 'development') {
          logger.info('[TeamProvider] Membership load complete, membershipLoading=false', {
            teamId: activeTeamId,
          });
        }
      }
    };

    void doInitialLoad();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, activeTeamId]);

  // Set active team ID and update user profile
  const setActiveTeamId = async (teamId: string) => {
    if (!user) {
      throw new Error('Not authenticated');
    }

    setUserProfileLoading(true);
    setError(null);

    try {
      // 1. Write canonical source of truth
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        activeTeamId: teamId,
        updatedAt: serverTimestamp(),
      });

      // 2. Refresh custom claims via callable CF
      const functions = getFunctions(undefined, 'europe-west1');
      const refreshClaims = httpsCallable(functions, 'refreshUserClaimsCallable');
      await refreshClaims({});

      // 3. Force client token refresh so new claims are in the next request
      await user.getIdToken(true);

      // 4. Update app state — only after token is confirmed fresh
      setActiveTeamIdState(teamId);

      logger.info('[setActiveTeamId] Team switched successfully', { teamId });
    } catch (err) {
      logger.error('[setActiveTeamId] Failed to switch team', err);
      setError(err instanceof Error ? err.message : 'Team switch failed');
      throw err;
    } finally {
      setUserProfileLoading(false);
    }
  };

  // Mark onboarding as completed optimistically (prevents guard redirect loops)
  const markOnboardingCompleted = () => {
    logger.info('[TeamProvider] Marking onboarding as completed (optimistic)');
    setOnboardingJustCompleted(true);
  };

  /**
   * Boot stability: Retry boot after hard timeout or error
   * Restarts the boot cycle by incrementing bootRunId and resetting state
   */
  const retryBoot = useCallback(() => {
    logger.info('[TeamProvider] Retrying boot', {
      previousBootRunId: bootRunIdRef.current,
      previousStatus: membershipLoadStatus,
      previousError: bootErrorCode,
    });

    // Increment boot run ID to start new cycle
    bootRunIdRef.current += 1;
    bootStartTimeRef.current = Date.now();
    bootFinalizedRef.current = false;
    clearBootWatchdogs();

    // Reset state to trigger re-load
    updateMembershipStatus('loading', 'retry_boot');
    setBootErrorCode(null);
    setError(null);
    setUserProfileLoading(true);
    setMembershipQueryCompleted(false);

    // Force re-run of the user profile loading effect
    // This will trigger the membership query again
  }, [bootErrorCode, clearBootWatchdogs, updateMembershipStatus]);

  // ============================================================================
  // Keep currentStateRef in sync with state (for watchdog to read fresh values)
  // ============================================================================
  useEffect(() => {
    currentStateRef.current = {
      userProfileLoading,
      teamDocLoading,
      membershipLoading,
      membershipQueryCompleted,
      activeTeamId,
      team,
      isMember,
      error,
      role,
    };
  }, [
    userProfileLoading,
    teamDocLoading,
    membershipLoading,
    membershipQueryCompleted,
    activeTeamId,
    team,
    isMember,
    error,
    role,
  ]);

  // ============================================================================
  // Boot stability: Soft (5s) + Hard (15s) timeout watchdog
  // Soft timeout: transitions to 'slow' state (non-fatal, shows "still loading" message)
  // Hard timeout: transitions to 'error' state with MEMBERSHIP_TIMEOUT code
  // ============================================================================
  useEffect(() => {
    // Only start watchdog when user is authenticated
    if (!user) {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      return;
    }

    const currentBootRunId = bootRunIdRef.current;
    const softTimeoutMs = 5000;
    const hardTimeoutMs = 15000;

    // Soft timeout: transition to 'slow' state
    softTimeoutRef.current = setTimeout(() => {
      // Check if boot cycle is still current and not finalized
      if (bootRunIdRef.current !== currentBootRunId || bootFinalizedRef.current) {
        return;
      }

      const state = currentStateRef.current;
      const isStillLoading =
        state.userProfileLoading || state.teamDocLoading || state.membershipLoading;
      const hasValidTeam = state.activeTeamId && state.team && state.isMember;

      if (isStillLoading && !hasValidTeam && !state.membershipQueryCompleted) {
        const elapsedMs = Date.now() - bootStartTimeRef.current;
        logger.info('[TeamProvider] Soft timeout reached', {
          bootRunId: currentBootRunId,
          membershipStatus: 'slow',
          elapsedMs,
        });
        updateMembershipStatus('slow', 'soft_timeout');
      }
    }, softTimeoutMs);

    // Hard timeout: transition to 'error' state
    hardTimeoutRef.current = setTimeout(() => {
      // Check if boot cycle is still current and not finalized
      if (bootRunIdRef.current !== currentBootRunId || bootFinalizedRef.current) {
        return;
      }

      const state = currentStateRef.current;
      const isStillLoading =
        state.userProfileLoading || state.teamDocLoading || state.membershipLoading;
      const hasValidTeam = state.activeTeamId && state.team && state.isMember;
      const membershipQueryDone = state.membershipQueryCompleted;

      if (isStillLoading && !hasValidTeam) {
        const provider = getProviderForLogging();
        const route = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
        const elapsedMs = Date.now() - bootStartTimeRef.current;

        logger.error('[TeamProvider] Hard timeout reached', {
          bootRunId: currentBootRunId,
          membershipStatus: 'error',
          errorCode: 'MEMBERSHIP_TIMEOUT',
          provider,
          route,
          uid: maskId(user.uid),
          userProfileLoading: state.userProfileLoading,
          teamDocLoading: state.teamDocLoading,
          membershipLoading: state.membershipLoading,
          membershipQueryCompleted: state.membershipQueryCompleted,
          activeTeamId: maskId(state.activeTeamId),
          elapsedMs,
        });

        // Force resolve all loading states
        // Mark as finalized to prevent further state updates
        bootFinalizedRef.current = true;
        setUserProfileLoading(false);
        setTeamDocLoading(false);
        setMembershipLoading(false);
        updateMembershipStatus('error', 'hard_timeout');
        setBootErrorCode('MEMBERSHIP_TIMEOUT');

        // Only set NO_TEAM if membership query definitively completed with empty result
        if (!state.activeTeamId) {
          if (membershipQueryDone) {
            setError('NO_TEAM');
          } else {
            setError('MEMBERSHIP_QUERY_TIMEOUT');
          }
        }
      }
    }, hardTimeoutMs);

    return () => {
      // Cleanup on unmount or user change
      clearBootWatchdogs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally only depends on user to avoid restarting timer
  }, [user]);

  // Compute isOnboardingInProgress: true if user is in any onboarding state
  // This is used by guards to show loading instead of error screens during onboarding
  // FIX: This prevents the "Access Denied" flicker for new users
  //
  // TRANSIENT RACE WINDOW (email/password new users):
  // atomicRegistration() writes team + membership in a single batch, then navigates
  // to /verify-email. After email verification the user lands on /dashboard.
  // TeamContext reads users/{uid}.activeTeamId (profileHasTeam=true) and then queries
  // users/{uid}/memberships. If the Firestore auth token hasn't fully propagated yet,
  // that query can return PERMISSION_DENIED — even though the user genuinely has a team.
  // In that case we must NOT show the error screen; we must keep showing the loader
  // (the retry logic in doInitialLoad will recover automatically).
  const isOnboardingInProgress =
    // Explicit onboarding states from profile
    onboardingState === 'needs_team' ||
    onboardingState === 'invited' ||
    // OAuth user waiting for bootstrap to complete
    (!!user && isOAuthUser(user) && !oauthBootstrapComplete) ||
    // User has no team and no definitive error yet (still resolving)
    (!activeTeamId && !error && !membershipQueryCompleted) ||
    // TRANSIENT RACE: profile says user has a team but membership query hit a permission
    // error — this is a transient auth-token-propagation race, NOT a real error.
    // Keep showing loader; the retry/watchdog will resolve it.
    (profileHasTeam &&
      (error === 'MEMBERSHIP_PERMISSION_DENIED' || error === 'MEMBERSHIP_QUERY_FAILED'));

  // ============================================================================
  // Issue 7 Fix: Invalidate React Query cache when role or plan changes
  // This ensures UI reflects the latest permissions after admin changes
  // ============================================================================
  useEffect(() => {
    const currentPlan = team?.plan ?? null;

    // Check if role changed (excluding initial load)
    if (prevRoleRef.current !== null && role !== prevRoleRef.current) {
      logger.info('[TeamProvider] Role changed, invalidating queries', {
        from: prevRoleRef.current,
        to: role,
      });
      // Invalidate all queries to refresh data with new permissions
      void queryClient.invalidateQueries();
    }

    // Check if plan changed (excluding initial load)
    if (prevPlanRef.current !== null && currentPlan !== prevPlanRef.current) {
      logger.info('[TeamProvider] Plan changed, invalidating queries', {
        from: prevPlanRef.current,
        to: currentPlan,
      });
      // Invalidate all queries to refresh data with new plan limits
      void queryClient.invalidateQueries();
    }

    // Update refs for next comparison
    prevRoleRef.current = role;
    prevPlanRef.current = currentPlan;
  }, [role, team?.plan, queryClient]);

  const value: TeamContextValue = {
    activeTeamId,
    team,
    loading,
    isMember,
    role,
    memberData,
    setActiveTeamId,
    error,
    entitlementInvalid,
    entitlementInvalidReason,
    onboardingJustCompleted,
    markOnboardingCompleted,
    onboardingState,
    isOnboardingInProgress,
    profileHasTeam,
    isDeactivated,
    isMemberDisabled,
    membershipLoadStatus,
    bootErrorCode,
    retryBoot,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

/**
 * Hook to access team context
 *
 * @throws Error if used outside TeamProvider
 */
export function useTeam(): TeamContextValue {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}

/**
 * Hook to get active team ID (convenience)
 *
 * @returns Active team ID or null
 */
export function useActiveTeamId(): string | null {
  const { activeTeamId } = useTeam();
  return activeTeamId;
}

/**
 * Hook to access active team context (canonical export)
 *
 * CANONICAL: This is the primary hook for accessing active team data.
 * Returns the full TeamContextValue including activeTeamId, team, role, etc.
 *
 * @throws Error if used outside TeamProvider
 */
export function useActiveTeam(): TeamContextValue {
  return useTeam();
}

/**
 * Hook to check if user is team admin
 *
 * @returns True if user is owner or admin
 */
export function useIsTeamAdmin(): boolean {
  const { role } = useTeam();
  return isAdminRole(role as TeamRole);
}
